import React from 'react';
import { connect } from 'react-redux';
import { Redirect, Route, Switch } from 'react-router-dom';
import queryString from 'query-string';
import { getBooleanConfig } from '@zg-rentals/frontend-config/client';
import ChecklistPage from './ChecklistPage';
import EmptyLeasePage from './EmptyLeasePage';
import RentalProtectionAreYouSurePage from './RentalProtectionAreYouSurePage';
import RentalProtectionIneligibleLeaseSelectedPage from './RentalProtectionIneligibleLeaseSelected';
import RentalProtectionUnsignedLeaseWarningPage from './RentalProtectionUnsignedLeaseWarning';
import MoveInCostsPage from './MoveInCostsPage';
import RentAmountPage from './RentAmountPage';
import RentalProtectionDifferentValueThanLeaseWarningPage from './RentalProtectionDifferentValueWarning';
import DueDatePage from './DueDatePage';
import TermLengthPage from './TermLengthPage';
import RentalProtectionDifferentTermThanLeaseWarningPage from './RentalProtectionDifferentTermWarning';
import AccountInfoPage from './AccountInfoPage';
import InviteTenantPage from './InviteTenantPage';
import LeaseSelectorPage from './LeaseSelectorPage';
import TaxVerificationPage from './TaxVerificationPage';
import PhoneVerificationPage from './PhoneVerificationPage';
import KnowledgeBasedAuthenticationPage from './KnowledgeBasedAuthenticationPage';
import ConfirmationPage from './ConfirmationPage';
import PostConfirmationPage from './PostConfirmationPage';
import ExitPaymentSetupPage from './ExitPaymentSetup';
import paths from '@rental-manager/pathsManifest';
import { push } from 'redux-first-history';
import { closeNotification } from '@common/actions/NotificationActions';
import { getIreneDashboard } from '@rental-manager/actions/requests';
import { getWizardScreen, WizardShell } from './WizardShell';
import { WizardEditOverlayContext } from './WizardShell/context';
import type { WizardEditOverlayValue } from './WizardShell/context';
import { RouteWithAuth } from '@common/components/RouteWithAuth';
import type { IClusterSettingsModel } from '../../models/Types';
import type { Location } from 'history';

type Props = {
  baseUrl: string;
  listing: Record<string, unknown>;
  propertyId: string;
  landlordStatus: string;
  clusterSettings: IClusterSettingsModel;
  router: Record<string, unknown>;
  acceptTerms: boolean;
  location: Location;
  hasLeases: boolean;
  dispatch: (...args: Array<any>) => any;
};

class OnboardingModalRoutes extends React.Component<Props> {
  static defaultProps = {
    acceptTerms: false,
  };

  modalProps: {
    baseUrl: string;
    listing: Record<string, unknown>;
    propertyId: string;
    landlordStatus: string;
    clusterSettings: IClusterSettingsModel;
    acceptTerms: boolean;
  };

  constructor(props) {
    super(props);
    this.modalProps = {
      baseUrl: props.baseUrl,
      listing: props.listing,
      propertyId: props.propertyId,
      landlordStatus: props.landlordStatus,
      clusterSettings: props.clusterSettings,
      acceptTerms: props.acceptTerms,
    };
  }

  ChecklistRoute = (): React.ReactNode => (
    <RouteWithAuth
      path={this.props.baseUrl + paths.onboarding.checklistPage}
      exact
      render={(routeProps) => {
        return <ChecklistPage {...routeProps} {...this.modalProps} />;
      }}
    />
  );

  EmptyLeaseRoute = (): React.ReactNode => (
    <RouteWithAuth
      path={this.props.baseUrl + paths.onboarding.emptyLeasePage}
      exact
      render={(routeProps) => {
        return <EmptyLeasePage {...routeProps} {...this.modalProps} />;
      }}
    />
  );

  RentalProtectionAreYouSureRoute = (): React.ReactNode => (
    <RouteWithAuth
      path={this.props.baseUrl + paths.onboarding.areYouSurePage}
      exact
      render={(routeProps) => {
        return <RentalProtectionAreYouSurePage {...routeProps} {...this.modalProps} />;
      }}
    />
  );

  RentalProtectionIneligibleLeaseSelectedRoute = (): React.ReactNode => (
    <RouteWithAuth
      path={this.props.baseUrl + paths.onboarding.ineligibleLeaseSelected}
      exact
      render={(routeProps) => {
        return <RentalProtectionIneligibleLeaseSelectedPage {...routeProps} {...this.modalProps} />;
      }}
    />
  );

  RentalProtectionUnsignedLeaseWarningRoute = (): React.ReactNode => (
    <RouteWithAuth
      path={this.props.baseUrl + paths.onboarding.unsignedLeaseWarning}
      exact
      render={(routeProps) => {
        return <RentalProtectionUnsignedLeaseWarningPage {...routeProps} {...this.modalProps} />;
      }}
    />
  );

  RentalProtectionDifferentValueThanLeaseWarningRoute = (): React.ReactNode => (
    <RouteWithAuth
      path={this.props.baseUrl + paths.onboarding.differentValueThanLeaseWarningPage}
      exact
      render={(routeProps) => {
        return <RentalProtectionDifferentValueThanLeaseWarningPage {...routeProps} {...this.modalProps} />;
      }}
    />
  );

  RentalProtectionDifferentTermThanLeaseWarningRoute = (): React.ReactNode => (
    <RouteWithAuth
      path={this.props.baseUrl + paths.onboarding.differentTermThanLeaseWarningPage}
      exact
      render={(routeProps) => {
        return <RentalProtectionDifferentTermThanLeaseWarningPage {...routeProps} {...this.modalProps} />;
      }}
    />
  );

  LeaseSelectorRoute = (): React.ReactNode => (
    <RouteWithAuth
      path={this.props.baseUrl + paths.onboarding.leaseSelectorPage}
      exact
      render={(routeProps) => {
        const searchString = routeProps.location.search;
        const queryParams = queryString.parse(searchString);
        const acceptTermsParam = queryParams.acceptTerms;

        // Redirect if user has not clicked "accept terms"
        if (!this.props.acceptTerms && acceptTermsParam !== 'true') {
          return <Redirect to={{ pathname: this.props.baseUrl, search: '' }} />;
        }

        return <LeaseSelectorPage {...routeProps} {...this.modalProps} />;
      }}
    />
  );

  RentAmountRoute = (): React.ReactNode => (
    <RouteWithAuth
      path={this.props.baseUrl + paths.onboarding.rentAmountPage}
      exact
      render={(routeProps) => {
        const searchString = routeProps.location.search;
        const queryParams = queryString.parse(searchString);
        const acceptTermsParam = queryParams.acceptTerms;

        // Redirect if user has not clicked "accept terms"
        if (!this.props.acceptTerms && acceptTermsParam !== 'true') {
          return <Redirect to={{ pathname: this.props.baseUrl, search: '' }} />;
        }

        return <RentAmountPage {...routeProps} {...this.modalProps} />;
      }}
    />
  );

  DueDateRoute = (): React.ReactNode => (
    <RouteWithAuth
      path={this.props.baseUrl + paths.onboarding.dueDatePage}
      exact
      render={(routeProps) => {
        const searchString = routeProps.location.search;
        const queryParams = queryString.parse(searchString);
        const acceptTermsParam = queryParams.acceptTerms;

        // Redirect if user has not clicked "accept terms"
        if (!this.props.acceptTerms && acceptTermsParam !== 'true') {
          return <Redirect to={{ pathname: this.props.baseUrl, search: '' }} />;
        }

        return <DueDatePage {...routeProps} {...this.modalProps} />;
      }}
    />
  );

  TermLengthRoute = (): React.ReactNode => (
    <RouteWithAuth
      path={this.props.baseUrl + paths.onboarding.termLengthPage}
      exact
      render={(routeProps) => {
        const searchString = routeProps.location.search;
        const queryParams = queryString.parse(searchString);
        const acceptTermsParam = queryParams.acceptTerms;

        // Redirect if user has not clicked "accept terms"
        if (!this.props.acceptTerms && acceptTermsParam !== 'true') {
          return <Redirect to={{ pathname: this.props.baseUrl, search: '' }} />;
        }

        return <TermLengthPage {...routeProps} {...this.modalProps} />;
      }}
    />
  );

  AccountInfoRoute = (): React.ReactNode => (
    <RouteWithAuth
      path={this.props.baseUrl + paths.onboarding.accountInfoPage}
      exact
      render={(routeProps) => {
        return <AccountInfoPage {...routeProps} {...this.modalProps} />;
      }}
    />
  );
  MoveInCostsRoute = (): React.ReactNode => (
    <RouteWithAuth
      path={this.props.baseUrl + paths.onboarding.moveInCostsPage}
      exact
      render={(routeProps) => {
        return <MoveInCostsPage {...routeProps} {...this.modalProps} />;
      }}
    />
  );

  InviteTenantRoute = (): React.ReactNode => (
    <RouteWithAuth
      path={this.props.baseUrl + paths.onboarding.inviteTenantPage}
      exact
      render={(routeProps) => {
        return <InviteTenantPage {...routeProps} {...this.modalProps} />;
      }}
    />
  );

  TaxVerificationRoute = (): React.ReactNode => (
    <RouteWithAuth
      path={this.props.baseUrl + paths.onboarding.taxVerificationPage}
      exact
      render={(routeProps) => {
        return <TaxVerificationPage {...routeProps} {...this.modalProps} />;
      }}
    />
  );

  PhoneVerificationRoute = (): React.ReactNode => (
    <RouteWithAuth
      path={this.props.baseUrl + paths.onboarding.phoneVerificationPage}
      exact
      render={(routeProps) => {
        return <PhoneVerificationPage {...routeProps} {...this.modalProps} />;
      }}
    />
  );

  KnowledgeBasedAuthenticationRoute = (): React.ReactNode => (
    <RouteWithAuth
      path={this.props.baseUrl + paths.onboarding.knowledgeBasedAuthenticationPage}
      exact
      render={(routeProps) => {
        return <KnowledgeBasedAuthenticationPage {...routeProps} {...this.modalProps} />;
      }}
    />
  );

  OnboardingConfirmationRoute = (): React.ReactNode => (
    <RouteWithAuth
      path={this.props.baseUrl + paths.onboarding.confirmationPage}
      exact
      render={(routeProps) => {
        return <ConfirmationPage {...routeProps} {...this.modalProps} />;
      }}
    />
  );

  PostConfirmationRoute = (): React.ReactNode => (
    <RouteWithAuth
      path={this.props.baseUrl + paths.onboarding.postConfirmationPage}
      exact
      render={(routeProps) => {
        return <PostConfirmationPage {...routeProps} {...this.modalProps} />;
      }}
    />
  );

  ExitPaymentSetupRoute = (): React.ReactNode => (
    <RouteWithAuth
      path={this.props.baseUrl + paths.onboarding.exitPaymentSetup}
      exact
      render={(routeProps) => {
        return <ExitPaymentSetupPage {...routeProps} {...this.modalProps} />;
      }}
    />
  );

  CatchAllRoute = (): React.ReactNode => (
    <Route
      path="*"
      render={(routeProps) => {
        if (routeProps.location.pathname !== this.props.baseUrl) {
          return <Redirect to={{ pathname: this.props.baseUrl, search: '' }} />;
        }
        return null;
      }}
    />
  );

  componentDidMount() {
    const isVisualRefreshEnabled = getBooleanConfig('isVisualRefreshEnabled', false);
    if (isVisualRefreshEnabled) {
      document.documentElement.setAttribute('data-panda-theme', 'zillow');
    }
  }

  componentWillUnmount() {
    document.documentElement.setAttribute('data-panda-theme', 'legacy-zillow');
  }

  exitPaymentSetup = (): void => {
    const { dispatch, baseUrl, propertyId } = this.props;
    dispatch(closeNotification());
    dispatch(push(baseUrl));
    dispatch(getIreneDashboard({ propertyId }));
  };

  returnToReview = (): void => {
    const { dispatch, baseUrl } = this.props;
    dispatch(closeNotification());
    dispatch(push(baseUrl + paths.onboarding.confirmationPage));
  };

  editOverlay: WizardEditOverlayValue = { onClose: this.returnToReview };

  /* Sub-steps pushed from inside the overlay (due date → term length, move-in costs → KBA) carry
     fromWhere but not the title, so the title sticks from the review's Edit until the overlay closes. */
  getEditOverlay(editTitle: string | undefined): WizardEditOverlayValue {
    const title = editTitle ?? this.editOverlay.title;
    if (title !== this.editOverlay.title) {
      this.editOverlay = { onClose: this.returnToReview, title };
    }
    return this.editOverlay;
  }

  render(): React.ReactNode {
    const { location, baseUrl } = this.props;
    const isFullScreenWizard =
      getBooleanConfig('isFullScreenPaymentSetupWizardEnabled', false) && Boolean(getWizardScreen(location.pathname));

    if (!isFullScreenWizard) {
      return this.renderRoutes();
    }

    /* Edits launched from the review screen stay on the review: the shell keeps rendering the
       confirmation route while the edited step renders outside the shell, where WizardScreen
       falls back to the md PaymentsModal and stacks on top. */
    const locationState = location.state as { fromWhere?: string; editTitle?: string } | undefined;
    const isEditingFromReview = locationState?.fromWhere === 'onboardingReview';
    const shellPathname = isEditingFromReview ? baseUrl + paths.onboarding.confirmationPage : location.pathname;

    return (
      <>
        <WizardShell
          activePathname={shellPathname}
          hiddenCategories={this.props.hasLeases ? undefined : ['Lease']}
          onSaveAndExit={this.exitPaymentSetup}
        >
          {this.renderRoutes(
            isEditingFromReview ? { ...location, pathname: shellPathname, state: undefined } : location,
          )}
        </WizardShell>
        {isEditingFromReview && (
          <WizardEditOverlayContext.Provider value={this.getEditOverlay(locationState?.editTitle)}>
            {this.renderRoutes()}
          </WizardEditOverlayContext.Provider>
        )}
      </>
    );
  }

  renderRoutes(location: Location = this.props.location): React.ReactNode {
    const { acceptTerms } = this.props;

    return (
      <Switch location={location}>
        {this.ChecklistRoute()}
        {acceptTerms && this.EmptyLeaseRoute()}
        {acceptTerms && this.LeaseSelectorRoute()}
        {acceptTerms && this.RentalProtectionAreYouSureRoute()}
        {acceptTerms && this.RentalProtectionIneligibleLeaseSelectedRoute()}
        {acceptTerms && this.RentalProtectionUnsignedLeaseWarningRoute()}
        {acceptTerms && this.RentAmountRoute()}
        {acceptTerms && this.RentalProtectionDifferentValueThanLeaseWarningRoute()}
        {acceptTerms && this.ExitPaymentSetupRoute()}
        {acceptTerms && this.DueDateRoute()}
        {acceptTerms && this.TermLengthRoute()}
        {acceptTerms && this.RentalProtectionDifferentTermThanLeaseWarningRoute()}
        {acceptTerms && this.AccountInfoRoute()}
        {acceptTerms && this.InviteTenantRoute()}
        {acceptTerms && this.TaxVerificationRoute()}
        {acceptTerms && this.PhoneVerificationRoute()}
        {acceptTerms && this.MoveInCostsRoute()}
        {acceptTerms && this.KnowledgeBasedAuthenticationRoute()}
        {acceptTerms && this.OnboardingConfirmationRoute()}
        {acceptTerms && this.PostConfirmationRoute()}
        {this.CatchAllRoute()}
      </Switch>
    );
  }
}

export default connect((state: { router; payments }, ownProps: { propertyId: string }) => ({
  location: state.router.location,
  hasLeases: (state.payments?.landlord?.onboarding?.[ownProps.propertyId]?.leasesForProperty?.length ?? 0) > 0,
}))(OnboardingModalRoutes);
